import {  Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MapService } from "../../../../services/map.service";
import { Device } from "../../../../model/device";

import * as mapboxgl from 'mapbox-gl';
import TinyColors from "tinycolor2";
import {AuthGuard} from '../../../../common/auth-guard.service';
import * as moment from "moment";
import {HttpClient} from "@angular/common/http";
import * as turf from "@turf/turf";
import { Point} from "geojson";
import {translate} from "@ngneat/transloco";
import {Router} from "@angular/router";
import {MapLayerControl} from "../../../../model/custom/map/map-layer-control";
import {CustomDatePipe} from "../../../../shared/pipes/custom-date.pipe";

@Component({
  selector: 'app-device-nettopo',
  templateUrl: './device-nettopo.component.html',
  styleUrls: ['./device-nettopo.component.scss']
})
export class DeviceNettopoComponent implements OnInit, OnDestroy {

  public readonly DEFAULT_ZOOM = 14;
  public readonly DEFAULT_LATITUDE = 39.984;
  public readonly DEFAULT_LONGITUDE = -0.020;

  @Input()
  public device: Device;


  public data: any;
  public nodes: any[] = [];
  public edges: any[] = [];
  public links: any[];
  public codeColors : any[] = [];
  public communicationsGraphData: any;
  public lostFramesGraphData: any;
  public drillDownGraphData: any;
  public totalPacketsGraphData: any;
  public categories : string[];

  public pageLoading: boolean = true;

  public map: mapboxgl.Map;
  public layers: any[];
  public selectedLayer;

  constructor(private mapService: MapService,
              private http: HttpClient,
              private router: Router,
              private customDatePipe: CustomDatePipe,
              public authGuard: AuthGuard) {}

  ngOnInit(): void {
    this.load();
    this.layers = [{label: translate("nettopo.communications"), value: "communications"}, {label: translate("nettopo.distances"), value: "distances"},
      {label: translate("nettopo.coverage"), value: "coverage"}, {label: translate("nettopo.lastCommunication"), value: "lastCommunication"}]
    this.categories= [ translate("nettopo.frames")];
  }

  private load() {
    this.map = this.mapService.buildMap(this.DEFAULT_ZOOM, this.DEFAULT_LATITUDE, this.DEFAULT_LONGITUDE, 'devTopoMap', true, { name: 'globe' });
    this.map.on('style.load', () => {
      this.map.setFog({}); // Set the default atmosphere style
    });

    this.http.post<any>(`/catalog/reports/nettopo`,this.buildAggQuery()).toPromise().then( (aggRes) => {
      this.data = aggRes.aggregations.edges;
      this.parseAggResponse();
    });

  }

  private parseAggResponse(){
    this.data.buckets.forEach( edge => {
      const source = edge.max_doc.hits.hits[0]._source;
      const totalToCommunications = this.nodes[source.to]?.totalToCommunications !== undefined ? this.nodes[source.to].totalToCommunications + edge.doc_count : edge.doc_count;
      const totalFromCommunications = this.nodes[source.from]?.totalCommunications !== undefined ? this.nodes[source.from].totalCommunications + edge.doc_count : edge.doc_count;
      const fromNode = {totalCommunications: totalFromCommunications, isImported: source.toIntegrationIdentifier === 'VIRTUAL_GW' || source.toIntegrationIdentifier === null, latitude: source.fromLatitude, longitude: source.fromLongitude , code: source.fromIntegrationCode, identifier: source.fromIntegrationIdentifier};
      const toNode = {isGW: source.throughGw, isHUB: source.throughHub, totalCommunications : totalToCommunications, latitude: source.toLatitude , longitude: source.toLongitude , code: source.toIntegrationCode, identifier: source.toIntegrationIdentifier};
      this.nodes[source.from] = fromNode;
      this.nodes[source.to] = toNode;

      this.edges[edge.key] = { origin: source.fromIntegrationCode, from: source.from, to: source.to, communications: edge.doc_count, throughGw: source.throughGw, throughHub: source.throughHub,
        line: [  [toNode.longitude, toNode.latitude], [fromNode.longitude, fromNode.latitude] ],
        distance: !toNode.latitude ? '' : turf.length(turf.lineString([[fromNode.longitude, fromNode.latitude], [toNode.longitude, toNode.latitude]])).toFixed(3) + " km",
        coverage: source.rssi ?? '' , target: source.toIntegrationIdentifier, lastPacket: this.customDatePipe.transform(source["@timestamp"])};


    });

    let colors = TinyColors("#407ec9").monochromatic(Reflect.ownKeys(this.nodes).length);

    Object.entries(this.nodes).forEach( ([key, value], index) => {
      this.codeColors[value.identifier] = { id: key, color: colors[index].toHexString()};
    });

    const edgeLines = [];
    const nodePoints = [];
    const links = [];
    Object.entries(this.edges).forEach( ([key, value], index) => {
      links.push({target: value.target, communications: value.communications, distance: value.distance, coverage: value.coverage, lastCommunication: value.lastPacket});
      if(value.line[0][0]) {
        edgeLines.push(
          {
            type: 'Feature',
            properties: {
              color: this.getCoverageColorOrDefault(value.coverage, '#4264fb', value.origin),
              'dash-line': [0, 4, 3],
              ...value
            },
            geometry: {
              type: 'LineString',
              coordinates: [value.line[1], value.line[0]],
            }
          }
        );
      }
    });
    Object.entries(this.nodes).forEach(([key, value], index) => {
      if(value.latitude) {
        nodePoints.push(
          {
            type: 'Feature',
            properties: {
              color: value.isGW ? '#03c211' : value.isHUB ? '#acb900' : value.isImported ? '#42bafb' : '#4264fb',
              description:
                '<strong>' + key + '</strong><p>Total communications: ' + value.totalCommunications + '</p>'
            },
            geometry: {
              type: 'Point',
              coordinates: [value.longitude, value.latitude]
            }
          }
        );
      }
    });

    if (this.map.loaded()) {
      this.buildMapSourcesAndLayers(nodePoints, edgeLines);
    } else {
      this.map.on('load', () => this.buildMapSourcesAndLayers(nodePoints, edgeLines));
    }

    this.links = links;
    this.buildGraphs();
  }

  public displayLayer(layer: string) {
    this.hidAllLayers();
    this.map.setLayoutProperty(layer, "visibility", "visible");
  }

  private getCoverageColorOrDefault(coverage: number, defaultColor: string, integrationCode : string) : string {

    if(coverage != null) {
      if(integrationCode === "LORA") {
        if(coverage > -100) {
          return '#03c239';
        } else if (coverage > -110) {
          return '#5fc203';
        } else if (coverage > -120) {
          return '#b5c203';
        } else {
          return '#c20303';
        }
      } else if(integrationCode === "IOTSENS") {
        if(coverage > -60) {
          return '#03c239';
        } else if (coverage > -70) {
          return '#5fc203';
        } else if (coverage > -90) {
          return '#b5c203';
        } else {
          return '#c20303';
        }
      }

    }

    return defaultColor;
  }

  private buildMapSourcesAndLayers(nodePoints: any, edgeLines: any): void {
    this.map.addSource('edges', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: edgeLines
      }
    });
    this.map.addSource('nodes', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: nodePoints
      }
    });
    this.map.addLayer({
      id: 'edges',
      type: 'line',
      source: 'edges',
      paint: {
        'line-width': 4,
        'line-color': ['get', 'color'],
        'line-dasharray': ['get', 'dash-line']
      }
    });
    this.map.addLayer({
      id: 'nodes',
      type: 'circle',
      source: 'nodes',
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 6,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    });

    this.map.addLayer({
      "id": "distances",
      "type": "symbol", "source": "edges",
      "layout": {
        "symbol-placement": "line", "text-font": ["Open Sans Regular"], "text-size": 14 ,
        "text-field": '{distance}',
      },
      "paint": {
        'text-halo-width': 3,
        'text-halo-color': '#ffffff'
      }
    });

    this.map.addLayer({
      "id": "communications",
      "type": "symbol", "source": "edges",
      "layout": {
        "symbol-placement": "line", "text-font": ["Open Sans Regular"], "text-size": 14 ,
        "text-field": '{communications} packets',
      },
      "paint": {
        'text-halo-width': 3,
        'text-halo-color': '#ffffff'
      }
    });

    this.map.addLayer({
      "id": "coverage",
      "type": "symbol", "source": "edges",
      "layout": {
        "symbol-placement": "line", "text-font": ["Open Sans Regular"], "text-size": 14 ,
        "text-field": '{coverage}',
      },
      "paint": {
        'text-halo-width': 3,
        'text-halo-color': '#ffffff'
      }
    });

    this.map.addLayer({
      "id": "lastCommunication",
      "type": "symbol", "source": "edges",
      "layout": {
        "symbol-placement": "line", "text-font": ["Open Sans Regular"], "text-size": 14 ,
        "text-field": '{lastPacket}',
      },
      "paint": {
        'text-halo-width': 3,
        'text-halo-color': '#ffffff'
      }
    });

    const popupNodes = new mapboxgl.Popup({closeButton: false, closeOnClick: false});
    this.map.on('mouseenter', 'nodes', (e) => {
      this.map.getCanvas().style.cursor = 'pointer';

      const point: Point = e.features[0].geometry as unknown as Point;
      const coordinates = point.coordinates.slice();
      const description = e.features[0].properties.description;

      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }
      popupNodes.setLngLat([coordinates[0], coordinates[1]]).setHTML(description).addTo(this.map);
    });
    this.map.on('mouseleave', 'nodes', () => {
      this.map.getCanvas().style.cursor = '';
      popupNodes.remove();
    });

     this.map.addControl(new MapLayerControl(document.getElementById('map-legend')), 'bottom-right');

    if(nodePoints.length > 0) {
      let minLat = Math.min(...nodePoints.map(node => node.geometry.coordinates[1]));
      let maxLat = Math.max(...nodePoints.map(node => node.geometry.coordinates[1]));
      let minLon = Math.min(...nodePoints.map(node => node.geometry.coordinates[0]));
      let maxLon = Math.max(...nodePoints.map(node => node.geometry.coordinates[0]));
      this.map.fitBounds([minLon, minLat, maxLon, maxLat], {maxDuration: 1, padding: 15});
    }

    this.hidAllLayers();

  }

  private hidAllLayers() {
    this.map.setLayoutProperty("distances", "visibility", "none");
    this.map.setLayoutProperty("communications", "visibility", "none");
    this.map.setLayoutProperty("coverage", "visibility", "none");
    this.map.setLayoutProperty("lastCommunication", "visibility", "none");
  }

  ngOnDestroy(): void {
    this.map?.remove();
    this.map = undefined;
  }

  private buildGraphs() {

    let nettopoQuery = this.buildAggQuery();
    nettopoQuery.size= 0 ;
    nettopoQuery.aggs.edges = { terms : {field:'@edge.keyword',size:100000000} , aggs: { days: { date_histogram: { field: "@timestamp", interval: "1d", time_zone: this.device.timezone, min_doc_count: 0 }}}};


    let framesQuery = this.buildLostFrameQuery();

    const communicationsDataSeries = [];
    const lostFramesDataSeries = [];

    Promise.all([
      this.http.post<any>(`/catalog/reports/nettopo`, nettopoQuery).toPromise(),
      this.http.post<any>(`/catalog/reports/lostframes`, framesQuery).toPromise(),
      this.http.post<any>(`/catalog/reports/frameticraw`, framesQuery).toPromise()
    ]).then(values => {

      values[0].aggregations.edges.buckets.forEach( data => {
        let serieData = [];
        let node = this.edges[data.key]?.target;
        let color = this.codeColors[node]??{color:"#407ec9"};
        data.days.buckets.forEach( dayData => {
          serieData.push({ x: dayData.key, y: dayData.doc_count, color: color.color})
        });
        communicationsDataSeries.push({ column: {"stack": 0}, data: serieData , name : node, color: color.color} );
      });
      this.communicationsGraphData = communicationsDataSeries;

      let serieData = [];
      let drillDown = [];
      let totalLoss = 0;

      values[1].hits.hits.forEach( dayData => {
        let timestamp = moment(dayData._source["@timestamp"]);
        let day = timestamp.format("YYYYMMDD")
        drillDown.push({name: "frameCnt", id: day, data: [], type: 'line', marker: { enabled : true }});
        serieData.push({x: timestamp.valueOf(), name: timestamp.valueOf(), y: dayData._source.frameLoss, drilldown:  day});
        totalLoss += Number.parseFloat(dayData._source.frameLoss);
      });
      lostFramesDataSeries.push( { data: serieData, name: translate("nettopo.lostframes")});

      values[2].hits.hits.forEach( data => {
        let timestamp = moment(data._source["@timestamp"]);
        let day = timestamp.format("YYYYMMDD")
        drillDown.find( elem => elem.id === day)?.data.push({x: timestamp.valueOf(), y: data._source.frameCnt})
      });

      this.lostFramesGraphData = lostFramesDataSeries;
      drillDown.forEach( serie => serie.data = serie.data.sort((n1, n2)=> n1.x - n2.x));

      drillDown.forEach( serie => {
        let newPoints = [];
        serie.data.forEach((point, index) => {
          if (index > 0) {
            if (point.y - serie.data[index - 1].y > 1) {
              point.color = '#e4002b';
              serie.data[index - 1].color = '#e4002b';
              newPoints.push({x: point.x-1, y: null})
            }
          }
        });
        serie.data.push(...newPoints);
      })
      drillDown.forEach( serie => serie.data = serie.data.sort((n1, n2)=> n1.x - n2.x));
      this.drillDownGraphData = { breadcrumbs: {
          showFullPath: false,
        }, drillUpButton: {
          position: {
            align: 'left'
          },
          theme: {
            fill: 'white',
            'stroke-width': 0,
            stroke: 'white',
          }
        }
        , series : drillDown };

      this.totalPacketsGraphData = [
        {name: translate("nettopo.lost"), data: [ totalLoss ], color: '#e4002b'},
        {name: translate("nettopo.received"), data: [values[2].hits.total.value ], color: "#03c211"}]

      this.pageLoading = false;
    });
  }

  private buildAggQuery(): any {
    let baseQuery = {aggs:{edges:{terms:{field:'@edge.keyword',size:100000000},aggs:{max_doc:{top_hits:{size:1,sort:[{'@timestamp':{order:'desc'}}]}}}}},size:0,_source:{excludes:[]},stored_fields:['*'],script_fields:{},docvalue_fields:[{field:'@timestamp',format:'date_time'}],query:{bool:{must:[],filter:[],should:[],must_not:[]}}}

    baseQuery.query.bool.must.push({
      range: {
        "@timestamp": {
          format: "strict_date_optional_time",
          gte: moment.utc().subtract(7, "d").toDate(),
          lte: moment.utc().toDate()
        }
      }
    });

    baseQuery.query.bool.must.push({
      "match_phrase": {
        "fromUuid.keyword": {
          "query": this.device.uuid
        }
      }
    });

    return baseQuery;

  }

  private buildLostFrameQuery(): any {
    let baseQuery = {"size":  10000, "query":{"bool":{"must":[],"filter":[],"should":[],"must_not":[]}}}
    baseQuery.query.bool.must.push({
      range: {
        "@timestamp": {
          format: "strict_date_optional_time",
          gte: moment.utc().subtract(7, "d").toDate(),
          lte: moment.utc().toDate()
        }
      }
    });

    baseQuery.query.bool.must.push({
      "match_phrase": {
        "deviceUuid.keyword": {
          "query": this.device.uuid
        }
      }
    });

    return baseQuery;

  }
}
